import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LaneData } from '../models/Models';
import { Translation } from '../helper/WinfireLiveConstHelper';
import { ShootOffView } from './ShootOffBanner';
import './ShootOffSpotlight.css';

/** How long the popup stays open after the tie is broken, so spectators see the decisive shot. */
const FINISHED_HOLD_MS = 6000;
/** Must match the `sos-fade-out` animation duration in ShootOffSpotlight.css */
const FADE_OUT_DURATION_MS = 400;

interface ShootOffSpotlightProps {
    /** The shoot-off to show, or null when there is none (the popup closes). */
    view: ShootOffView | null;
    laneData: LaneData[] | undefined | null;
    /** Renders the target tile of a lane in shoot-off mode (the same tile the grid shows). */
    renderTile: (laneData: LaneData) => React.ReactNode;
}

/**
 * Centered popup that enlarges the targets of the lanes in a shoot-off, so spectators can follow
 * every shoot-off shot.
 *
 * Opens as soon as a shoot-off is running. Lanes that took part but are safe (they shot better than
 * the lowest value of a shoot-off round of a 3+ way tie) stay visible but dimmed. Once the tie is
 * broken the eliminated target gets an "eliminated" stamp; the popup stays open for
 * FINISHED_HOLD_MS and then fades out (the banner keeps announcing the result).
 */
const ShootOffSpotlight: React.FC<ShootOffSpotlightProps> = ({ view, laneData, renderTile }) => {
    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);
    // All lanes seen in this shoot-off, in lane order: the lane set of the server shrinks to the
    // lanes still shooting during a 3+ way tie, but the popup keeps the whole tie in view.
    const [lanes, setLanes] = useState<number[]>([]);
    const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const status = view?.status ?? null;
    const laneKey = (view?.laneNumbers ?? []).slice().sort((a, b) => a - b).join(',');

    // Collect the lanes of the running shoot-off; forget them once the shoot-off is over.
    useEffect(() => {
        if (!view) return;
        setLanes(prev => {
            const merged = Array.from(new Set([...prev, ...view.laneNumbers])).sort((a, b) => a - b);
            return merged.length === prev.length ? prev : merged;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [laneKey]);

    useEffect(() => {
        const clearTimers = () => {
            if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null; }
            if (leaveTimerRef.current) { clearTimeout(leaveTimerRef.current); leaveTimerRef.current = null; }
        };

        const close = () => {
            setIsLeaving(true);
            leaveTimerRef.current = setTimeout(() => {
                setIsOpen(false);
                setIsLeaving(false);
                if (!status) setLanes([]);
            }, FADE_OUT_DURATION_MS); // must match sos-fade-out duration in ShootOffSpotlight.css
        };

        clearTimers();
        if (status === 'running') {
            setIsLeaving(false);
            setIsOpen(true);
        } else if (status === 'finished') {
            // Keep the decisive shot on screen for a moment before closing.
            setIsLeaving(false);
            setIsOpen(true);
            holdTimerRef.current = setTimeout(close, FINISHED_HOLD_MS);
        } else if (isOpen) {
            close();
        } else {
            setLanes([]);
        }
        return clearTimers;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    if (!isOpen || lanes.length === 0) return null;

    const isFinished = status === 'finished';
    const shootingLanes = view?.laneNumbers ?? [];

    // Layout: up to 3 targets side by side, more in two rows. Tile height is limited by both the
    // viewport height and the width available per column, keeping the tile's portrait aspect.
    const count = lanes.length;
    const columns = count <= 3 ? count : Math.ceil(count / 2);
    const rows = count <= 3 ? 1 : 2;
    const tileAspect = 0.8; // width / height of a target tile
    const maxHeightVh = rows === 1 ? 72 : 38;
    const maxHeightByWidthVw = Math.floor(88 / columns / tileAspect);
    const slotHeight = `min(${maxHeightVh}vh, ${maxHeightByWidthVw}vw)`;

    const title = isFinished
        ? t(Translation.SHOOTOFF_FINISHED)
        : `${t(Translation.PHASE_SHOOTOFF)} · ${t(Translation.SHOOTOFF_ROUND, { round: view?.round ?? 1 })}`;

    return (
        <div className={`sos-backdrop${isLeaving ? ' sos-backdrop--leaving' : ''}`} aria-hidden="true">
            <div className={`sos-panel${isFinished ? ' sos-panel--finished' : ''}`}>
                <div className="sos-title">{title}</div>
                <div className="sos-slots" style={{ gridTemplateColumns: `repeat(${columns}, auto)` }}>
                    {lanes.map(laneNr => {
                        const ld = laneData?.find(l => l.laneNumber === laneNr);
                        if (!ld) return null;
                        const isShooting = !isFinished && shootingLanes.includes(laneNr);
                        const isEliminated = isFinished && ld.isEliminated === true;
                        const slotClass = 'sos-slot'
                            + (isShooting ? ' sos-slot--shooting' : '')
                            + (!isFinished && !isShooting ? ' sos-slot--safe' : '')
                            + (isEliminated ? ' sos-slot--eliminated' : '')
                            + (isFinished && !isEliminated ? ' sos-slot--survived' : '');
                        return (
                            <div
                                key={laneNr}
                                className={slotClass}
                                style={{ height: slotHeight, width: `calc(${slotHeight} * ${tileAspect})` }}
                            >
                                {renderTile(ld)}
                                {isEliminated && (
                                    <div className="sos-stamp">
                                        <span>{t(Translation.SHOOTOFF_ELIMINATED_STAMP)}</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ShootOffSpotlight;
